import React, { Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import ApolloClient from 'graphql-common/ApolloClient';
import DashboardLayout from 'layouts/DashboardLayout/DashboardLayout';
import { APP_URLS } from 'constants/urls';
import { SiteTabViewMode, SiteViewMode } from 'routes/Sites/enums';
import { BreadCrumbsProvider } from '@lib/components/BreadCrumbs/BreadCrumbsContext';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import AntdConfigProvider from '@lib/components/Antd/ConfigProvider';
import RouteErrorElement from '@lib/components/RouteErrorElement/RouteErrorElement';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import { UserViewMode } from 'routes/Users/enums';
import FormChecklists from 'routes/FormChecklists/FormChecklists';
import FormChecklist from 'routes/FormChecklists/FormChecklist';
import Home from './Home/HomeContainer';
import WidgetTemplates from './Home/WidgetTemplatesContainer';
import WidgetTemplateCreate from './Home/WidgetTemplateCreateContainer';
import { HomeViewMode } from './Home/enums';
import TasksOverview from './Dashboard/TasksOverview/TasksOverview';
import TasksTimeline from './Dashboard/TasksTimeline/TasksTimeline';
import Monitoring from './Dashboard/Monitoring/Monitoring';
import { FormTemplateMode } from './FormTemplate/enums';
import FormTemplates from './FormTemplates/FormTemplatesContainer';
import FormTemplateBuilderFormContainer from './FormTemplate/FormTemplateBuilderFormContainer';
import FormTemplateCategories from './FormTemplateCategories/FormTemplateCategoriesContainer';
import FormTemplateViewContainer from './FormTemplate/FormTemplateViewContainer';
import { InterventionViewMode } from './Intervention/enums';
import Profile from './Profile/ProfileContainer';
import Tasks from './Tasks/TasksContainer';
import TasksApprovals from './TasksApprovals/TasksApprovalsContainer';
import Users from './Users/UsersContainer';
import User from './Users/UserContainer';
import Sites from './Sites/SiteListContainer';
import Site from './Sites/SiteContainer';
import SiteTabsPage from './Sites/SiteTabsPage';
import AssetDocumentation from './Assets/AssetDocumentation/AssetDocumentation';
import Assets from './Assets/AssetList/AssetListContainer';
import AssetView from './Assets/Asset/AssetContainer';
import AssetForm from './Assets/Asset/AssetFormContainer';
import { AssetViewMode } from './Assets/Asset/enums';
import AssetCounters from './Assets/AssetCounters/AssetCountersContainer';
import AssetCategories from './Assets/AssetCategories/AssetCategoriesContainer';
import InterventionCategories from './InterventionCategories/InterventionCategoriesContainer';
import Intervention from './Intervention/InterventionContainer';
import InterventionAction from './Intervention/InterventionActionContainer';
import TaskCreate from './Task/TaskCreateContainer';
import TaskEdit from './Task/TaskEditContainer';
import TaskTabsPage from './Task/TaskTabsPage';
import { TaskViewMode } from './Task/enums';
import Interventions from './Interventions/InterventionsContainer';
import Teams from './Teams/TeamsContainer';
import Team from './Teams/TeamContainer';
import { TeamViewMode } from './Teams/enums';
import Departments from './Departments/DepartmentsContainer';
import Department from './Departments/DepartmentContainer';
import { DepartmentViewMode } from './Departments/enums';
import PublicApi from './PublicApi/PublicApi';
import DataImport from './DataImport/DataImport';
import Sectors from './Sectors/SectorList/SectorsContainer';
import SectorForm from './Sectors/Sector/SectorFormContainer';
import SectorView from './Sectors/Sector/SectorViewContainer';
import { SectorViewMode } from './Sectors/Sector/enums';
import CompanyDomains from './CompanyDomains/CompanyDomains';
import CompanyPlan from './CompanyPlan/CompanyPlan';
import SsoIntegrations from './SsoIntegrations/SsoIntegrations';
import SsoIntegrationForm from './SsoIntegrations/SsoIntegrationForm';
import SsoIntegrationView from './SsoIntegrations/SsoIntegrationView';
import Reminder from './Reminder/Reminder';
import DocumentList from './Documentation/DocumentList/DocumentList';
import DocumentAnalytics from './Documentation/DocumentAnalytics/DocumentAnalytics';
import DocumentApprovals from './Documentation/DocumentApprovals/DocumentApprovals';
import DocumentCategories from './Documentation/DocumentCategories/DocumentCategories';
import DocumentForm from './Documentation/DocumentForm/DocumentForm';
import DocumentImport from './Documentation/DocumentImport/DocumentImport';
import DocumentVersionForm from './Documentation/DocumentVersionForm/DocumentVersionForm';
import DocumentView from './Documentation/DocumentView/DocumentView';
import ErrorPage from './ErrorPage/ErrorPage';
import NotFoundPage from './NotFoundPage/NotFoundPage';
import ProtectedRoute from './ProtectedRoute';

const { VITE_ENVIRONMENT } = import.meta.env;

const router = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedRoute />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        path: '/',
        element: <DashboardLayout />,
        children: [
          {
            path: APP_URLS.dashboard.home.path,
            element: <Home viewMode={HomeViewMode.View} />,
          },
          {
            path: APP_URLS.dashboard.homeEdit.path,
            element: <Home viewMode={HomeViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.homeWidgets.path,
            element: <WidgetTemplates />,
          },
          {
            path: APP_URLS.dashboard.homeWidgetsCreate.path,
            element: <WidgetTemplateCreate />,
          },
          {
            path: APP_URLS.dashboard.overview.path,
            element: <TasksOverview />,
          },
          {
            path: APP_URLS.dashboard.timeline.path,
            element: <TasksTimeline />,
          },
          {
            path: APP_URLS.dashboard.monitoring.path,
            element: <Monitoring />,
          },
          {
            path: APP_URLS.dashboard.profile.path,
            element: <Profile />,
          },
          VITE_ENVIRONMENT === 'DEV'
            ? {
                path: APP_URLS.dashboard.publicApi.path,
                element: <PublicApi />,
              }
            : {},
          {
            path: APP_URLS.dashboard.dataImport.path,
            element: <DataImport />,
          },
          {
            path: APP_URLS.dashboard.users.index.path,
            element: <Users />,
          },
          {
            path: APP_URLS.dashboard.users.view.path,
            element: <User viewMode={UserViewMode.View} />,
          },
          {
            path: APP_URLS.dashboard.users.viewAccess.path,
            element: <User viewMode={UserViewMode.ViewAccess} />,
          },
          {
            path: APP_URLS.dashboard.users.editAccess.path,
            element: <User viewMode={UserViewMode.EditAccess} />,
          },
          {
            path: APP_URLS.dashboard.teams.index.path,
            element: <Teams />,
          },
          {
            path: APP_URLS.dashboard.teams.add.path,
            element: <Team viewMode={TeamViewMode.Add} />,
          },
          {
            path: APP_URLS.dashboard.teams.edit.path,
            element: <Team viewMode={TeamViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.teams.view.path,
            element: <Team viewMode={TeamViewMode.ViewDetails} />,
          },
          {
            path: APP_URLS.dashboard.teams.members.path,
            element: <Team viewMode={TeamViewMode.ViewMembers} />,
          },
          {
            path: APP_URLS.dashboard.teams.departments.path,
            element: <Team viewMode={TeamViewMode.ViewDepartments} />,
          },
          {
            path: APP_URLS.dashboard.departments.index.path,
            element: <Departments />,
          },
          {
            path: APP_URLS.dashboard.departments.add.path,
            element: <Department viewMode={DepartmentViewMode.Add} />,
          },
          {
            path: APP_URLS.dashboard.departments.edit.path,
            element: <Department viewMode={DepartmentViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.departments.view.path,
            element: <Department viewMode={DepartmentViewMode.ViewDetails} />,
          },
          {
            path: APP_URLS.dashboard.departments.members.path,
            element: <Department viewMode={DepartmentViewMode.ViewMembers} />,
          },
          {
            path: APP_URLS.dashboard.departments.accessGroups.path,
            element: (
              <Department viewMode={DepartmentViewMode.ViewAccessGroups} />
            ),
          },
          {
            path: APP_URLS.dashboard.departments.tasks.path,
            element: <Department viewMode={DepartmentViewMode.ViewTasks} />,
          },
          {
            path: APP_URLS.dashboard.departments.interventions.path,
            element: (
              <Department viewMode={DepartmentViewMode.ViewInterventions} />
            ),
          },
          {
            path: APP_URLS.dashboard.sites.index.path,
            element: <Sites />,
          },
          {
            path: APP_URLS.dashboard.sites.add.path,
            element: <Site viewMode={SiteViewMode.Add} />,
          },
          {
            path: APP_URLS.dashboard.sites.edit.getPathWithQuery(),
            element: <Site viewMode={SiteViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.sites.view.getPathWithQuery(),
            element: <SiteTabsPage viewMode={SiteTabViewMode.About} />,
          },
          {
            path: APP_URLS.dashboard.sites.viewCodeOfConduct.path,
            element: <Site viewMode={SiteViewMode.ViewCodeOfConduct} />,
          },
          {
            path: APP_URLS.dashboard.sites.editCodeOfConduct.path,
            element: <Site viewMode={SiteViewMode.EditCodeOfConduct} />,
          },
          {
            path: APP_URLS.dashboard.sites.areas.getPathWithQuery(),
            element: <SiteTabsPage viewMode={SiteTabViewMode.Areas} />,
          },
          {
            path: APP_URLS.dashboard.sites.users.getPathWithQuery(),
            element: <SiteTabsPage viewMode={SiteTabViewMode.Users} />,
          },
          {
            path: APP_URLS.dashboard.sectors.index.path,
            element: <Sectors />,
          },
          {
            path: APP_URLS.dashboard.sectors.add.getPathWithQuery(),
            element: <SectorForm mode={SectorViewMode.Create} />,
          },
          {
            path: APP_URLS.dashboard.sectors.edit.getPathWithQuery(),
            element: <SectorForm mode={SectorViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.sectors.view.getPathWithQuery(),
            element: <SectorView mode={SectorViewMode.Details} />,
          },
          {
            path: APP_URLS.dashboard.sectors.sites.path,
            element: <SectorView mode={SectorViewMode.Sites} />,
          },
          {
            path: APP_URLS.dashboard.sectors.users.path,
            element: <SectorView mode={SectorViewMode.Users} />,
          },
          {
            path: APP_URLS.dashboard.formTemplates.index.path,
            element: <FormTemplates />,
          },
          {
            path: APP_URLS.dashboard.formTemplates.archived.path,
            element: <FormTemplates />,
          },
          {
            path: APP_URLS.dashboard.formTemplates.categories.path,
            element: <FormTemplateCategories />,
          },
          {
            path: APP_URLS.dashboard.formTemplates.add.path,
            element: (
              <FormTemplateBuilderFormContainer
                viewMode={FormTemplateMode.Add}
              />
            ),
          },
          {
            path: APP_URLS.dashboard.formTemplates.edit.path,
            element: (
              <FormTemplateBuilderFormContainer
                viewMode={FormTemplateMode.Edit}
              />
            ),
          },
          {
            path: APP_URLS.dashboard.formTemplates.editVersion.path,
            element: (
              <FormTemplateBuilderFormContainer
                viewMode={FormTemplateMode.EditVersion}
                key={FormTemplateMode.EditVersion}
              />
            ),
          },
          {
            path: APP_URLS.dashboard.formTemplates.viewVersion.path,
            element: (
              <FormTemplateViewContainer viewMode={FormTemplateMode.View} />
            ),
          },
          {
            path: APP_URLS.dashboard.formTemplates.viewLinkedDocuments.path,
            element: (
              <FormTemplateViewContainer
                viewMode={FormTemplateMode.ViewLinkedDocuments}
              />
            ),
          },
          {
            path: APP_URLS.dashboard.formTemplates.viewLinkedTasks.path,
            element: (
              <FormTemplateViewContainer
                viewMode={FormTemplateMode.ViewLinkedTasks}
              />
            ),
          },
          {
            path: APP_URLS.dashboard.formTemplates.duplicate.path,
            element: (
              <FormTemplateBuilderFormContainer
                viewMode={FormTemplateMode.Duplicate}
              />
            ),
          },
          {
            path: APP_URLS.dashboard.assets.index.path,
            element: <Assets />,
          },
          {
            path: APP_URLS.dashboard.assets.categories.path,
            element: <AssetCategories />,
          },
          {
            path: APP_URLS.dashboard.interventions.index.path,
            element: <Interventions />,
          },
          {
            path: APP_URLS.dashboard.interventions.add.path,
            element: <Intervention viewMode={InterventionViewMode.Add} />,
          },
          {
            path: APP_URLS.dashboard.interventions.edit.path,
            element: <Intervention viewMode={InterventionViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.interventions.view.path,
            element: <Intervention viewMode={InterventionViewMode.View} />,
          },
          {
            path: APP_URLS.dashboard.interventions.viewActions.path,
            element: (
              <Intervention viewMode={InterventionViewMode.ViewActions} />
            ),
          },
          {
            path: APP_URLS.dashboard.interventions.actionAdd.path,
            element: (
              <InterventionAction viewMode={InterventionViewMode.AddAction} />
            ),
          },
          {
            path: APP_URLS.dashboard.interventions.actionEdit.path,
            element: (
              <InterventionAction viewMode={InterventionViewMode.EditAction} />
            ),
          },
          {
            path: APP_URLS.dashboard.interventions.actionView.path,
            element: (
              <InterventionAction viewMode={InterventionViewMode.ViewAction} />
            ),
          },
          {
            path: APP_URLS.dashboard.interventions.categories.path,
            element: <InterventionCategories />,
          },
          {
            path: APP_URLS.dashboard.assets.add.path,
            element: <AssetForm viewMode={AssetViewMode.Add} />,
          },
          {
            path: APP_URLS.dashboard.assets.edit.getPathWithQuery(),
            element: <AssetForm viewMode={AssetViewMode.Edit} />,
          },
          {
            path: APP_URLS.dashboard.assets.view.getPathWithQuery(),
            element: <AssetView viewMode={AssetViewMode.View} />,
          },
          {
            path: APP_URLS.dashboard.assets.nomenclature.path,
            element: <AssetView viewMode={AssetViewMode.Nomenclatures} />,
          },
          {
            path: APP_URLS.dashboard.assets.counter.path,
            element: <AssetView viewMode={AssetViewMode.Counters} />,
          },
          {
            path: APP_URLS.dashboard.assets.documentation.path,
            element: <AssetView viewMode={AssetViewMode.Documentation} />,
          },
          {
            path: APP_URLS.dashboard.assets.interventions.path,
            element: <AssetView viewMode={AssetViewMode.Interventions} />,
          },
          {
            path: APP_URLS.dashboard.assets.counters.path,
            element: <AssetCounters />,
          },
          {
            path: APP_URLS.dashboard.assets.document.path,
            element: <AssetDocumentation />,
          },
          {
            path: APP_URLS.dashboard.tasks.index.path,
            element: <Tasks />,
          },
          {
            path: APP_URLS.dashboard.tasks.approvals.path,
            element: <TasksApprovals />,
          },
          {
            path: APP_URLS.dashboard.tasks.add.path,
            element: <TaskCreate />,
          },
          {
            path: APP_URLS.dashboard.tasks.edit.getPathWithQuery(),
            element: <TaskEdit />,
          },
          {
            path: APP_URLS.dashboard.tasks.duplicate.getPathWithQuery(),
            element: <TaskCreate isDuplicate />,
          },
          {
            path: APP_URLS.dashboard.tasks.view.getPathWithQuery(),
            element: <TaskTabsPage viewMode={TaskViewMode.Details} />,
          },
          {
            path: APP_URLS.dashboard.tasks.formView.getPathWithQuery(),
            element: <TaskTabsPage viewMode={TaskViewMode.Form} />,
          },
          {
            path: APP_URLS.dashboard.tasks.reporting.getPathWithQuery(),
            element: <TaskTabsPage viewMode={TaskViewMode.Reporting} />,
          },
          {
            path: APP_URLS.dashboard.tasks.interventions.path,
            element: <TaskTabsPage viewMode={TaskViewMode.Interventions} />,
          },
          {
            path: APP_URLS.dashboard.tasks.departments.path,
            element: <TaskTabsPage viewMode={TaskViewMode.Departments} />,
          },
          {
            path: APP_URLS.dashboard.formChecklists.index.path,
            element: <FormChecklists />,
          },
          {
            path: APP_URLS.dashboard.formChecklists.add.path,
            element: <FormChecklist />,
          },
          {
            path: APP_URLS.dashboard.formChecklists.edit.getPathWithQuery(),
            element: <FormChecklist isEdit />,
          },
          {
            path: APP_URLS.dashboard.companyDomains.index.path,
            element: <CompanyDomains />,
          },
          {
            path: APP_URLS.dashboard.documentation.index.path,
            element: <DocumentList />,
          },
          {
            path: APP_URLS.dashboard.documentation.analytics.path,
            element: <DocumentAnalytics />,
          },
          {
            path: APP_URLS.dashboard.documentation.approvals.path,
            element: <DocumentApprovals />,
          },
          {
            path: APP_URLS.dashboard.documentation.categories.path,
            element: <DocumentCategories />,
          },
          {
            path: APP_URLS.dashboard.documentation.add.path,
            element: <DocumentForm />,
          },
          {
            path: APP_URLS.dashboard.documentation.import.path,
            element: <DocumentImport />,
          },
          {
            path: APP_URLS.dashboard.documentation.edit.path,
            element: <DocumentForm isEdit />,
          },
          {
            path: APP_URLS.dashboard.documentation.view.path,
            element: <DocumentView />,
          },
          {
            path: APP_URLS.dashboard.documentation.createVersion.path,
            element: <DocumentVersionForm />,
          },
          {
            path: APP_URLS.dashboard.sso.index.path,
            element: <SsoIntegrations />,
          },
          {
            path: APP_URLS.dashboard.sso.add.path,
            element: <SsoIntegrationForm />,
          },
          {
            path: APP_URLS.dashboard.sso.edit.path,
            element: <SsoIntegrationForm isEdit />,
          },
          {
            path: APP_URLS.dashboard.sso.view.path,
            element: <SsoIntegrationView />,
          },
          {
            path: APP_URLS.dashboard.reminder.path,
            element: <Reminder />,
          },
          {
            path: APP_URLS.dashboard.companyPlan.path,
            element: <CompanyPlan />,
          },
          {
            path: APP_URLS.dashboard.unauthorized.path,
            element: <NotFoundPage status={ErrorPageStatusEnum.Status401} />,
          },
          {
            path: APP_URLS.dashboard.accessDenied.path,
            element: <ErrorPage status={ErrorPageStatusEnum.AccessDenied} />,
          },
          {
            path: APP_URLS.dashboard.index.path,
            index: true,
            element: <Navigate to={APP_URLS.dashboard.profile.path} replace />,
          },
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);

function App(): React.ReactElement {
  return (
    <ApolloProvider client={ApolloClient}>
      <AntdConfigProvider>
        <BreadCrumbsProvider>
          <Suspense fallback={<CircleLoader />}>
            <RouterProvider router={router} />
          </Suspense>
        </BreadCrumbsProvider>
      </AntdConfigProvider>
    </ApolloProvider>
  );
}

export default App;
